'use client';

import { useEffect, useRef } from 'react';
import { useReducedMotion } from 'motion/react';
import { Camera, Geometry, Mesh, Program, Renderer, Transform } from 'ogl';

/**
 * Işın alanı — React Bits "Beams" (MIT, https://reactbits.dev/backgrounds/beams).
 *
 * ── Neden three.js değil ogl ──────────────────────────────────────────────
 *
 * Kaynak sürüm `three` + `@react-three/fiber` + `@react-three/drei` istiyor;
 * üçü de bu projede kurulu değil ve yalnızca bir arka plan için ~600 KB'lık
 * bir bağımlılık zinciri açmak anlamsız. `ogl` (1.0.11) zaten kurulu ve
 * sitedeki diğer üç React Bits zemini de (`orb`, `gradient-waves`,
 * `dark-veil`) aynı kütüphaneye taşındı — bu dosya o sıranın dördüncüsü.
 *
 * Taşınan şey EFEKTİN KENDİSİ, kod değil:
 *   • Geometri birebir: `createStackedPlanesBufferGeometry` aynı algoritma —
 *     n adet şerit, aralarında boşluk yok, her şeridin UV'si rastgele
 *     kaydırılmış. Işınları ayıran şey o kaydırma: her şerit gürültünün
 *     başka bir evresini örneklediği için komşusundan farklı derinlikte
 *     dalgalanıyor.
 *   • Yer değiştirme birebir: Perlin gürültüsü (`cnoise`, Stefan Gustavson)
 *     köşeyi z'de itiyor, normal analitik olarak iki komşu örnekten
 *     hesaplanıyor.
 *   • Işıklandırma YENİDEN yazıldı. Kaynak `MeshStandardMaterial`in PBR
 *     zincirini genişletiyor; ogl'de öyle bir taban yok ve buraya fiziksel
 *     doğruluk da gerekmiyor — tek yön ışığı + tabanda yumuşak bir dolgu
 *     yetiyor.
 *
 * ── Neden AÇIK ────────────────────────────────────────────────────────────
 *
 * Kaynak koyu: siyah zemin, beyaz ışıkla yalanan siyah yüzeyler. Buradaki
 * kullanım (blog hero'su) açık zeminde, o yüzden ilişki ters çevrildi: taban
 * beyaza yakın, gölge tarafı nane grisine düşüyor. Yani ışınlar zeminden
 * KOYU değil, gölgeleri renkli. Sonuç: beyaz kâğıt üstünde eğik ışık.
 *
 * Alfa kanalı saydam kalıyor (`premultipliedAlpha`), bölümün CSS rampası
 * ışınların arasından görünüyor — canvas kendi zeminini getirmiyor.
 *
 * ── DESIGN.md'nin istedikleri (kaynakta olmayanlar) ───────────────────────
 *
 *   • **Azaltılmış hareket.** Kaynakta koşulsuz `requestAnimationFrame`.
 *     MotionConfig sonsuz döngüleri kapatmıyor (DESIGN.md §1), bu yüzden
 *     elle: tercih açıksa tek kare basılıp duruluyor. Alan kaybolmuyor —
 *     donmuş hâli de tam bir görüntü.
 *   • **Piksel bütçesi.** Kaynak `dpr={[1, 2]}` diyor. Burada çizim tamponu
 *     tavanla sınırlı; maliyet parça gölgelendiricide değil köşe
 *     gölgelendiricide (köşe başına üç `cnoise`), ama tampon büyüdükçe
 *     doldurma da büyüyor.
 *   • **Görünürlük.** Sekme gizliyken ve alan ekran dışındayken döngü
 *     duruyor. Kaynakta ikisi de yok.
 *   • **Ölçü kaynağı.** Kaynak Canvas'ın kendi resize'ına güveniyor; burada
 *     kabın kendisi `ResizeObserver` ile izleniyor — hero'nun yüksekliği
 *     metin sarınca da değişiyor, pencere ölçüsü değişmeden.
 */

export type BeamsProps = {
  className?: string;
  /** Şerit sayısı. Kaynak varsayılanı 12. */
  beamNumber?: number;
  /** Tek şeridin genişliği, dünya birimi. */
  beamWidth?: number;
  /** Şerit boyu. Kameranın gördüğü alandan uzun olmalı, yoksa uçları görünür. */
  beamHeight?: number;
  /** Gürültünün akış hızı. */
  speed?: number;
  /** Parça gölgelendiricideki film grenİ. */
  noiseIntensity?: number;
  /** Gürültü ölçeği — büyüdükçe dalgalar sıklaşıyor. */
  scale?: number;
  /** Alanın eğimi, derece. */
  rotation?: number;
  /** Yüzeyin taban rengi (ışık alan yüz). */
  baseColor?: string;
  /** Gölge tarafının rengi — açık zeminde bu renk "ışın" hissini taşıyor. */
  shadeColor?: string;
  /** Katmanın genel opaklığı. */
  opacity?: number;
};

/**
 * Çizim tamponu tavanı. 3.2 Mpx ≈ 2300×1400.
 *
 * `gradient-waves`ın tavanından (2.2 Mpx) yüksek olabiliyor çünkü orada
 * maliyet piksel başına 128 adıma kadar ışın yürütmeydi; burada parça
 * gölgelendirici birkaç satır ve asıl yük köşelerde — köşe sayısı ise tampon
 * boyutundan bağımsız (2424 köşe, sabit).
 */
const MAX_BUFFER_PIXELS = 3_200_000;

/** Şerit başına dikey bölüm. Dalganın yumuşaklığı buradan geliyor. */
const HEIGHT_SEGMENTS = 140;

/* --- Alan neden bu kadar büyük -------------------------------------------
   Kaynak sürümün ölçüleri (12 şerit × 2 birim = 24 genişlik, 15 boy) tam
   ekran bir tuval için kurulmuş. Bizde alan eğik (28°) ve geniş-alçak bir
   hero'nun içinde; o ölçülerle tabakanın KÖŞESİ ekranın içine giriyor ve
   orada alanın bittiği yer keskin bir diyagonal olarak görünüyordu
   (kullanıcı bildirdi, 12 Ağu 2026 — sol altta "oturmamış" bir çizgi).

   Kenarı yumuşatmak yanlış çözüm: sorun kenarın sertliği değil, kenarın
   GÖRÜNÜYOR olması. Doğrusu tabakayı her kırılma noktasında ekrandan büyük
   tutmak.

   Hesap — kamera z=20, fov 30° → görünür yükseklik 2·20·tan15° = 10.72
   birim; görünür genişlik = 10.72 × en/boy. Döndürülmüş bir dikdörtgenin
   yerel eksenlerdeki yarı-uzanımı:

     yarı-x = (G/2)·|cos θ| + (Y/2)·|sin θ|
     yarı-y = (G/2)·|sin θ| + (Y/2)·|cos θ|

   En kötü durum DESIGN.md §2'nin üst sınırı: 3440px genişlik, hero ~512px
   → en/boy 6.7 → G = 71.8, Y = 10.72. θ = 28° için yarı-x = 34.2,
   yarı-y = 21.6. Buradaki 40 şerit (yarı-x = 40) ve 48 boy (yarı-y = 24)
   ikisini de karşılıyor.

   Şerit GENİŞLİĞİ değişmiyor (hâlâ 2 birim), yani ışınlar aynı kalınlıkta;
   artan tek şey alanın kapladığı yer. Köşe maliyeti 40×141×2 ≈ 11 bin köşe,
   GPU için önemsiz. */
const DEFAULT_BEAM_NUMBER = 40;
const DEFAULT_BEAM_HEIGHT = 48;

const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m || !m[1] || !m[2] || !m[3]) return [1, 1, 1];
  return [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255];
};

/**
 * Yan yana dizilmiş şeritler — kaynak sürümdeki algoritmanın aynısı.
 *
 * Şeritler arasında boşluk YOK; onları birbirinden ayıran şey UV kaydırması.
 * `uvXOffset`/`uvYOffset` rastgele: her şerit gürültü alanının başka bir
 * yerinden örnekliyor, dolayısıyla komşusuyla aynı anda aynı derinliğe
 * gitmiyor. Boşluk bırakılsaydı şeritler tek tek çubuklara dönerdi; asıl
 * istenen, kesintisiz bir yüzeyin kendi içinde ışına ayrılması.
 *
 * `Math.random()` sunucuda ÇALIŞMIYOR: geometri yalnızca `useEffect` içinde,
 * yani tarayıcıda kuruluyor. Sunucu çıktısında canvas boş — hidrasyon farkı
 * doğmuyor.
 */
function stackedPlanes(count: number, width: number, height: number, segments: number) {
  const vertexCount = count * (segments + 1) * 2;
  const faceCount = count * segments * 2;

  const position = new Float32Array(vertexCount * 3);
  const uv = new Float32Array(vertexCount * 2);
  const index = new Uint32Array(faceCount * 3);

  let v = 0;
  let iOff = 0;
  let uvOff = 0;

  const totalWidth = count * width;
  const xBase = -totalWidth / 2;

  for (let i = 0; i < count; i++) {
    const x = xBase + i * width;
    const uvX = Math.random() * 300;
    const uvY = Math.random() * 300;

    for (let j = 0; j <= segments; j++) {
      const y = height * (j / segments - 0.5);
      position.set([x, y, 0, x + width, y, 0], v * 3);
      uv.set([uvX, j / segments + uvY, uvX + 1, j / segments + uvY], uvOff);

      if (j < segments) {
        index.set([v, v + 1, v + 2, v + 2, v + 1, v + 3], iOff);
        iOff += 6;
      }
      v += 2;
      uvOff += 4;
    }
  }

  return { position, uv, index };
}

/* Perlin gürültüsü — kaynaktan birebir (Stefan Gustavson, ashima/webgl-noise).
   "Düzeltilecek" bir yanı yok, sabitleri algoritmanın kendisi. */
const NOISE = /* glsl */ `
float random(in vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float valueNoise(in vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}
vec4 permute(vec4 x) { return mod(((x * 34.0) + 1.0) * x, 289.0); }
vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
vec3 fade(vec3 t) { return t * t * t * (t * (t * 6.0 - 15.0) + 10.0); }
float cnoise(vec3 P) {
  vec3 Pi0 = floor(P);
  vec3 Pi1 = Pi0 + vec3(1.0);
  Pi0 = mod(Pi0, 289.0);
  Pi1 = mod(Pi1, 289.0);
  vec3 Pf0 = fract(P);
  vec3 Pf1 = Pf0 - vec3(1.0);
  vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
  vec4 iy = vec4(Pi0.yy, Pi1.yy);
  vec4 iz0 = Pi0.zzzz;
  vec4 iz1 = Pi1.zzzz;
  vec4 ixy = permute(permute(ix) + iy);
  vec4 ixy0 = permute(ixy + iz0);
  vec4 ixy1 = permute(ixy + iz1);
  vec4 gx0 = ixy0 / 7.0;
  vec4 gy0 = fract(floor(gx0) / 7.0) - 0.5;
  gx0 = fract(gx0);
  vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
  vec4 sz0 = step(gz0, vec4(0.0));
  gx0 -= sz0 * (step(0.0, gx0) - 0.5);
  gy0 -= sz0 * (step(0.0, gy0) - 0.5);
  vec4 gx1 = ixy1 / 7.0;
  vec4 gy1 = fract(floor(gx1) / 7.0) - 0.5;
  gx1 = fract(gx1);
  vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
  vec4 sz1 = step(gz1, vec4(0.0));
  gx1 -= sz1 * (step(0.0, gx1) - 0.5);
  gy1 -= sz1 * (step(0.0, gy1) - 0.5);
  vec3 g000 = vec3(gx0.x, gy0.x, gz0.x);
  vec3 g100 = vec3(gx0.y, gy0.y, gz0.y);
  vec3 g010 = vec3(gx0.z, gy0.z, gz0.z);
  vec3 g110 = vec3(gx0.w, gy0.w, gz0.w);
  vec3 g001 = vec3(gx1.x, gy1.x, gz1.x);
  vec3 g101 = vec3(gx1.y, gy1.y, gz1.y);
  vec3 g011 = vec3(gx1.z, gy1.z, gz1.z);
  vec3 g111 = vec3(gx1.w, gy1.w, gz1.w);
  vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
  g000 *= norm0.x; g010 *= norm0.y; g100 *= norm0.z; g110 *= norm0.w;
  vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
  g001 *= norm1.x; g011 *= norm1.y; g101 *= norm1.z; g111 *= norm1.w;
  float n000 = dot(g000, Pf0);
  float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
  float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
  float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
  float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
  float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
  float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
  float n111 = dot(g111, Pf1);
  vec3 fade_xyz = fade(Pf0);
  vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
  vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
  float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
  return 2.2 * n_xyz;
}
`;

/* `getPos` / `getCurrentPos` / `getNormal` kaynaktaki `vertexHeader`ın aynısı:
   köşe z'de itiliyor, normal iki komşu örneğin çapraz çarpımından çıkıyor.
   `pos.x * 0.0` bilinçli — gürültü şerit boyunca x'e bağlı DEĞİL, yoksa
   ışınlar yatay olarak da dalgalanır ve dikey karakterini kaybederdi. */
const VERTEX = /* glsl */ `#version 300 es
in vec3 position;
in vec2 uv;

uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform mat3 normalMatrix;
uniform float uTime;
uniform float uSpeed;
uniform float uScale;
uniform float uHalfHeight;
uniform float uHalfWidth;

out vec3 vNormal;
out float vFade;

${NOISE}

float getPos(vec3 pos) {
  vec3 noisePos = vec3(pos.x * 0.0, pos.y - uv.y, pos.z + uTime * uSpeed * 3.0) * uScale;
  return cnoise(noisePos);
}

vec3 getCurrentPos(vec3 pos) {
  vec3 next = pos;
  next.z += getPos(pos);
  return next;
}

vec3 getNormal(vec3 pos) {
  vec3 cur = getCurrentPos(pos);
  vec3 nextX = getCurrentPos(pos + vec3(0.01, 0.0, 0.0));
  vec3 nextY = getCurrentPos(pos + vec3(0.0, -0.01, 0.0));
  return normalize(cross(normalize(nextY - cur), normalize(nextX - cur)));
}

void main() {
  vec3 transformed = position;
  transformed.z += getPos(position);

  vNormal = normalize(normalMatrix * getNormal(position));

  /* Uçlarda sönüm ARTIK yalnızca bir emniyet: alan (yukarıdaki hesapla) her
     kırılma noktasında ekrandan büyük, yani bu sönümün çalıştığı bölge
     görünür alanın dışında kalıyor. Eşik bu yüzden uçlara çok yakın (0.84);
     daha erken başlasaydı geniş ekranda alanın kenarı yine solmuş olarak
     görünürdü.

     Ekranda GÖRÜNEN yumuşama artık parça gölgelendiricide ve ekran
     uzayında (aşağıya bak) — kabın üstü ve altı, tabakanın nerede bittiğinden
     bağımsız olarak sönüyor. */
  float fy = 1.0 - smoothstep(0.84, 1.0, abs(position.y) / uHalfHeight);
  float fx = 1.0 - smoothstep(0.84, 1.0, abs(position.x) / uHalfWidth);
  vFade = fy * fx;

  gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
}
`;

/* Kaynağın PBR zinciri yerine tek yön ışığı: yüz ışığa döndükçe `uBase`e,
   sırtını döndükçe `uShade`e gidiyor. Açık zeminde okunan şey bu ikinci
   renk — ışın dediğimiz şey aslında gölgenin kendisi.

   Gren kaynaktaki `dithering_fragment` ekiyle aynı yerde ve aynı katsayıda
   (`/ 15.0`): geniş ve düz yüzeylerde 8 bitlik rampanın bantlaşmasını
   kırıyor. */
const FRAGMENT = /* glsl */ `#version 300 es
precision highp float;

uniform vec3 uBase;
uniform vec3 uShade;
uniform vec3 uLightDir;
uniform float uNoiseIntensity;
uniform float uOpacity;
uniform vec2 uResolution;

in vec3 vNormal;
in float vFade;

out vec4 fragColor;

float random(in vec2 st) {
  return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
}
float valueNoise(in vec2 st) {
  vec2 i = floor(st);
  vec2 f = fract(st);
  float a = random(i);
  float b = random(i + vec2(1.0, 0.0));
  float c = random(i + vec2(0.0, 1.0));
  float d = random(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

void main() {
  vec3 n = normalize(vNormal);
  /* İki yüzü de aydınlat: şeritler tek yüzlü ve dalgalanınca bir kısmı
     sırtını kameraya dönüyor; abs() olmadan o parçalar tamamen gölgede
     kalıp yüzeyi lekeli gösteriyor. */
  float diffuse = abs(dot(n, normalize(uLightDir)));
  diffuse = smoothstep(0.0, 1.0, diffuse);

  vec3 col = mix(uShade, uBase, diffuse);
  col -= valueNoise(gl_FragCoord.xy) / 15.0 * uNoiseIntensity;

  /* Üst ve alt kenarda sönüm — EKRAN uzayında, geometride değil. Tabakanın
     kendi uçları artık görüş alanının dışında (bkz. köşe hesabı), dolayısıyla
     "ışınlar nerede biter" sorusunu geometri değil kabın kendisi cevaplıyor.
     Böylece hero ne kadar alçalırsa alçalsın sönüm hep aynı oranda kalıyor.

     Alt pay üstten geniş: altta bölüm beyaz listeye bağlanıyor ve orada
     CSS'in kendi şeridi de var (globals.css, .bhero-body::after); ikisi
     üst üste binerek dikişi kapatıyor. */
  vec2 uv = gl_FragCoord.xy / uResolution;
  float edge = smoothstep(0.0, 0.16, uv.y) * smoothstep(0.0, 0.12, 1.0 - uv.y);

  float a = uOpacity * vFade * edge;
  fragColor = vec4(col * a, a);
}
`;

type Handle = {
  start: () => void;
  stop: () => void;
};

export function Beams({
  className,
  beamNumber = DEFAULT_BEAM_NUMBER,
  beamWidth = 2,
  beamHeight = DEFAULT_BEAM_HEIGHT,
  speed = 2,
  noiseIntensity = 1.4,
  scale = 0.2,
  rotation = 28,
  baseColor = '#ffffff',
  /* Gölge tonu: beyazdan yalnızca bir kademe koyu, ama yeşile çalıyor.
     Nötr gri denendi ve elendi — ışınlar orada "kirli cam" gibi duruyor,
     nane tonunda ise sayfanın kendi ışığı gibi okunuyor. */
  shadeColor = '#c4ded4',
  opacity = 0.95,
}: BeamsProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<Handle | null>(null);
  const reduce = useReducedMotion();

  /* --- Kurulum. Bir kez çalışır; renkler ve hız kurulum anında okunuyor. */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const renderer = new Renderer({
      webgl: 2,
      alpha: true,
      premultipliedAlpha: true,
      antialias: true,
      dpr: 1,
    });

    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    const canvas = gl.canvas as HTMLCanvasElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';
    container.appendChild(canvas);

    const camera = new Camera(gl, { fov: 30, near: 0.1, far: 120 });
    camera.position.z = 20;

    const scene = new Transform();
    scene.rotation.z = (rotation * Math.PI) / 180;

    const { position, uv, index } = stackedPlanes(
      beamNumber,
      beamWidth,
      beamHeight,
      HEIGHT_SEGMENTS,
    );

    const geometry = new Geometry(gl, {
      position: { size: 3, data: position },
      uv: { size: 2, data: uv },
      index: { data: index },
    });

    const program = new Program(gl, {
      vertex: VERTEX,
      fragment: FRAGMENT,
      transparent: true,
      /* Şeritler tek yüzlü ve dalgalandıkça bir kısmı arkasını dönüyor;
         yüz eleme açık kalsaydı o parçalarda delik açılırdı. */
      cullFace: false,
      /* Derinlik yazımı kapalı: yan yana duran şeritler saydam kenarlarıyla
         birbirini kesmesin. Tek bir mesh var, sıralama zaten sabit. */
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uSpeed: { value: speed },
        uScale: { value: scale },
        uHalfHeight: { value: beamHeight / 2 },
        uHalfWidth: { value: (beamNumber * beamWidth) / 2 },
        uNoiseIntensity: { value: noiseIntensity },
        uOpacity: { value: opacity },
        uResolution: { value: new Float32Array([1, 1]) },
        uBase: { value: new Float32Array(hexToRgb(baseColor)) },
        uShade: { value: new Float32Array(hexToRgb(shadeColor)) },
        /* Işık sağ üstten: sayfadaki diğer ışık kaynaklarıyla (hero rampası)
           aynı yön, yani sitede güneş tek yerde duruyor. */
        uLightDir: { value: new Float32Array([0.35, 0.6, 0.72]) },
      },
    });

    const mesh = new Mesh(gl, { geometry, program });
    /* Kırpma kapalı: köşeler gölgelendiricide yer değiştiriyor, ogl'nin
       hesapladığı sınır kutusu gerçek konumları bilmiyor. */
    mesh.frustumCulled = false;
    mesh.setParent(scene);

    const draw = () => renderer.render({ scene, camera });

    /* Tampon alanını tavana göre ölçekle — bkz. MAX_BUFFER_PIXELS. */
    const setSize = () => {
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width));
      const h = Math.max(1, Math.floor(rect.height));
      const raw = Math.min(window.devicePixelRatio || 1, 2);
      const area = w * h * raw * raw;
      renderer.dpr = area > MAX_BUFFER_PIXELS ? raw * Math.sqrt(MAX_BUFFER_PIXELS / area) : raw;
      renderer.setSize(w, h);

      /* 🔴 `setSize` canvas'ın CSS boyutunu PİKSEL olarak yeniden yazıyor
         (ogl/src/core/Renderer.js: `Object.assign(canvas.style, {width:
         width+'px', …})`). Kurulumdaki `100%` böylece ilk ölçümde eziliyor ve
         canvas o ANIN genişliğine çakılıyor; kap sonradan genişlerse (dikey
         kaydırma çubuğunun gelip gitmesi yeter) sağda boş bir şerit kalıyor.
         Kullanıcı 12 Ağu 2026'da bildirdi: hero'nun sağında ~72px beyaz bant.

         Yüzde geri yazılıyor: çizim tamponu yine ölçülen boyutta ama görüntü
         kabı her zaman dolduruyor. Ölçüm bir kare geride kalsa bile görünen
         tek şey ihmal edilebilir bir esneme, kesik değil.

         ⚠️ Aynı tuzak `orb.tsx`, `gradient-waves.tsx` ve `dark-veil.tsx`te de
         duruyor — oradaki kapların genişliği bugün değişmediği için ortaya
         çıkmadı. */
      canvas.style.width = '100%';
      canvas.style.height = '100%';

      camera.perspective({ aspect: w / h });
      /* Kenar sönümü ekran uzayında hesaplanıyor; çözünürlük çizim
         tamponundan okunuyor, CSS pikselinden değil — `gl_FragCoord` de o
         uzayda. */
      const res = program.uniforms.uResolution.value as Float32Array;
      res[0] = gl.drawingBufferWidth;
      res[1] = gl.drawingBufferHeight;
      draw();
    };

    const ro = new ResizeObserver(setSize);
    ro.observe(container);
    setSize();

    let raf = 0;
    let inView = true;
    let pageVisible = !document.hidden;
    /* Azaltılmış hareket ilk karede biliniyor olmalı: `useReducedMotion` ilk
       render'da null döner, bir kare animasyon sızardı. */
    let animate = !(
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    let last = performance.now();
    const time = program.uniforms.uTime as { value: number };

    /* Zaman mutlak damgadan değil DELTA ile ilerliyor (kaynak da öyle):
       döngü duraklayıp devam ettiğinde alan kaldığı yerden sürüyor, aradaki
       boşluk kadar sıçramıyor. Katsayı 0.1 kaynağın kendi ölçeği. */
    const loop = (t: number) => {
      const dt = Math.min((t - last) * 0.001, 0.1);
      last = t;
      time.value += 0.1 * dt;
      draw();
      raf = requestAnimationFrame(loop);
    };

    const start = () => {
      if (animate && inView && pageVisible && raf === 0) {
        last = performance.now();
        raf = requestAnimationFrame(loop);
      }
    };
    const stop = () => {
      if (raf !== 0) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    handleRef.current = {
      start: () => {
        animate = true;
        start();
      },
      stop: () => {
        animate = false;
        stop();
        draw();
      },
    };

    const io = new IntersectionObserver(
      (entries) => {
        inView = entries.some((entry) => entry.isIntersecting);
        if (inView) start();
        else stop();
      },
      { threshold: 0 },
    );
    io.observe(container);

    const onVisibility = () => {
      pageVisible = !document.hidden;
      if (pageVisible) start();
      else stop();
    };
    document.addEventListener('visibilitychange', onVisibility);

    start();

    return () => {
      stop();
      handleRef.current = null;
      ro.disconnect();
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.remove();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
    // Kurulum bir kez çalışır: prop'lar sabit, değişirlerse alan yeniden kurulur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --- Azaltılmış hareket. Alan kaybolmuyor, donuyor. --------------------- */
  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;
    if (reduce) handle.stop();
    else handle.start();
  }, [reduce]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
